import { useQuery, useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useContext, useState } from 'react';

import { ServiceCode, UserRequestStatus } from '@/shared/types';

import ModalContext from '@/contexts/Modal/ModalContext';
import { ModalName } from '@/contexts/Modal/modalTypes';

import { queryKeys } from '@/api/queryKeys';
import { FormDataType, UserRequest } from '@/api/types';

import { useFormDraft, UseFormDraftHookReturn } from './useFormDraft';

interface HookProps {
  serviceCode: ServiceCode;
  stepNumberToPay?: number;
  setData: (stateForm: Nullable<FormDataType>) => void;
  handleOnDraftContinue?: (stepNumberToOpen: number) => Promise<number>;
}

interface HookReturn {
  isLoading: boolean;
  currentRequestId: Nullable<UserRequest['id']>;
  userRequestDraft: Nullable<UserRequest>;
  onDraftCreate: (props: Parameters<UseFormDraftHookReturn['createDraft']>[0]) => Promise<Nullable<number>>;
  onDraftSave: (props: Pick<Parameters<UseFormDraftHookReturn['saveDraft']>[0], 'meta' | 'stateForm'>) => Promise<void>;
  onDraftContinue: (request_id: UserRequest['id']) => Promise<number>;
  onPaymentContinue: (request_id: UserRequest['id']) => Promise<number>;
  onDraftDelete: (request_id: UserRequest['id']) => Promise<void>;
}

export const useFormDraftTools = ({
  serviceCode,
  stepNumberToPay,
  setData,
  handleOnDraftContinue,
}: HookProps): HookReturn => {
  const { renderSpecificModal } = useContext(ModalContext);

  const { prefillFormData, createDraft, loadDraft, deleteDraft, saveDraft } = useFormDraft();
  const queryClient = useQueryClient();

  const { data: userRequestDraft, isLoading: isUserRequestDraftLoading } = useQuery({
    queryKey: queryKeys.userRequests.draft(serviceCode),
    queryFn: () => loadDraft(serviceCode),
  });

  const invalidateDraft = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.userRequests.draft(serviceCode) }),
    [queryClient, serviceCode],
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentRequestId, setCurrentRequestId] = useState<Nullable<UserRequest['id']>>(null);

  const onDraftDelete = useCallback(
    async (request_id: UserRequest['id']): Promise<void> => {
      try {
        setIsLoading(true);
        await deleteDraft(request_id);
        setData(null);
        setCurrentRequestId(null);
      } finally {
        setIsLoading(false);
      }
    },
    [deleteDraft, setData, setCurrentRequestId, setIsLoading],
  );

  const onDraftContinue = useCallback<HookReturn['onDraftContinue']>(
    async (request_id) => {
      try {
        setIsLoading(true);

        const prefilledFormData = await prefillFormData({
          draftId: request_id,
          handleInputChange: () => null,
        });

        const { parsedStateForm, meta, attachments } = prefilledFormData;

        await invalidateDraft();

        setData({ ...parsedStateForm, ...attachments });
        setCurrentRequestId(request_id);

        return handleOnDraftContinue ? await handleOnDraftContinue(meta!.current_step) : meta!.current_step;
      } catch (error) {
        throw new Error(`${error}`);
      } finally {
        setIsLoading(false);
      }
    },
    [prefillFormData, invalidateDraft, setData, handleOnDraftContinue, setIsLoading, setCurrentRequestId],
  );

  const onPaymentContinue = useCallback<HookReturn['onPaymentContinue']>(
    async (request_id) => {
      setCurrentRequestId(request_id);
      return stepNumberToPay ?? 1;
    },
    [setCurrentRequestId, stepNumberToPay],
  );

  const onDraftCreate = useCallback<HookReturn['onDraftCreate']>(async (props) => {
    return new Promise(async (resolve) => {
      try {
        setIsLoading(true);
        const freshlyCreatedDraftId = await createDraft(props);
        await invalidateDraft();

        setCurrentRequestId(freshlyCreatedDraftId);
        resolve(null);
      } catch (error) {
        const parsedError = JSON.parse(error as string);
        const { requestId, reason } = JSON.parse(parsedError.message) as {
          requestId: number;
          reason: UserRequestStatus.DRAFT | UserRequestStatus.PAYMENT;
        };

        const prefilledFormData = await prefillFormData({
          draftId: requestId.toString(),
          handleInputChange: () => null,
        });

        const { parsedStateForm } = prefilledFormData;
        if (isEmpty(parsedStateForm)) {
          resolve(await onDraftContinue(`${requestId}`));
          return;
        }

        switch (reason) {
          case UserRequestStatus.DRAFT:
            renderSpecificModal({
              modalName: ModalName.ServiceDraftExistingAlert,
              modalProps: {
                variant: 'new',
                serviceCode,
                startNewDraft: async () => {
                  await onDraftDelete(`${requestId}`);
                  await onDraftCreate(props);
                  resolve(null);
                },
                continueDraft: async () => {
                  resolve(await onDraftContinue(`${requestId}`));
                },
              },
            });
            return;

          case UserRequestStatus.PAYMENT: {
            renderSpecificModal({
              modalName: ModalName.PayOrLeaveAlert,
              modalProps: {
                variant: 'new',
                payButtonAction: () => {
                  setCurrentRequestId(`${requestId}`);
                  resolve(stepNumberToPay);
                },
                onDeleteCallback: async () => {
                  await onDraftDelete(`${requestId}`);
                  await onDraftCreate(props);
                  resolve(null);
                },
              },
            });
            return;
          }

          default:
            console.error(error);
            resolve(null);
            break;
        }
      } finally {
        setIsLoading(false);
      }
    });
  }, []);

  const onDraftSave: HookReturn['onDraftSave'] = useCallback(
    async (props) => {
      if (!userRequestDraft) return;

      try {
        setIsLoading(true);
        await saveDraft({
          draftId: currentRequestId ?? userRequestDraft.id,
          draftCurrentStep: userRequestDraft.meta?.current_step || 0,
          ...props,
        });
        await invalidateDraft();
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [saveDraft, invalidateDraft, setIsLoading],
  );

  return {
    isLoading: isLoading || isUserRequestDraftLoading,
    currentRequestId,
    userRequestDraft,
    onDraftCreate,
    onDraftSave,
    onDraftContinue,
    onPaymentContinue,
    onDraftDelete,
  };
};
